<template>
  <BModal
    id="modalLeadJourney"
    body-class="clever-modal"
    size="full"
    :centered="true"
    scrollable
    hide-header
    hide-footer
    hide-header-close
    @hidden="hide"
  >
    <div>
      <div class="clever-modal__header">
        <div class="clever-modal__header__heading">
          <h3 v-if="fullName">
            {{ fullName }}
          </h3>

          <span>
            <strong>ID {{ leadId }}</strong>

            <span
              v-if="product"
              :title="product"
            >
              | {{ product | truncate(90) }}
            </span>
          </span>
        </div>

        <button
          class="clever-modal__header__close"
          @click="$bvModal.hide('modalLeadJourney')"
        />
      </div>

      <template v-if="isLoading">
        <div class="clever-modal__loader">
          <BaseLoaderDefault/>
        </div>
      </template>

      <div
        v-else
        class="clever-modal__content"
      >
        <div class="lead-journey">
          <div class="lead-journey__status">
            <span
              class="tag-status"
              :class="`tag-status--${statusId}`"
            >
              {{ statusLabel }}
            </span>
          </div>

          <div class="lead-journey__timeline">
            <h3 class="lead-journey__timeline__heading">
              Etapas da jornada
            </h3>

            <ul
              v-if="timeline.length"
              class="lead-journey__timeline__steps"
            >
              <li
                v-for="step in timeline"
                :key="step['clh-created_at']"
                class="lead-journey__timeline__step"
              >
                <div
                  class="lead-journey__timeline__step__container"
                  :class="`journey-steps--${step['clh-fk_id_status']}`"
                >
                  <div
                    class="lead-journey__timeline__step__label"
                  >
                    {{ step['cs-label'] }}
                    <span v-if="step['clch-value']">
                      - {{ step['clch-value'] }}
                    </span>
                  </div>

                  <div class="lead-journey__timeline__step__when">
                    <strong>{{ step['clch-created_at'] || step['clh-created_at'] | date }}</strong> às {{ step['clch-created_at'] || step['clh-created_at'] | date('time') }}
                  </div>
                </div>
              </li>
            </ul>

            <BaseEmptyState v-else>
              <SvgIconListX/>

              <h3>
                As etapas desta jornada não podem ser exibidas.
              </h3>
            </BaseEmptyState>
          </div>

          <div
            v-if="isBotFinishedOrRefused"
            class="lead-journey__result"
          >
            <div class="lead-journey__result__header">
              Resultado: <strong>{{ statusLabel }}</strong>
            </div>

            <div class="lead-journey__result__details">
              <p>{{ dataset.leadContent.content.note_internal }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </BModal>
</template>

<script>
import { dateFilter, truncateFilter } from '@/utils/filters'
import BaseLoaderDefault from '@/components/base/BaseLoaderDefault'
import BaseEmptyState from '@/components/base/BaseEmptyState'
import { mapState, mapGetters } from 'vuex'
import axios from 'axios'
import { uniqBy } from 'lodash'

export default {
  name: 'CommonModalLeadJourney',

  components: {
    BaseLoaderDefault,
    BaseEmptyState
  },

  filters: {
    date: dateFilter,
    truncate: truncateFilter,

    tabulation (value) {
      return value
    }
  },

  props: {
    leadId: Number,
    statusId: Number,
    statusLabel: String,
    fullName: String,
    product: String
  },

  data () {
    return {
      isLoading: false,
      dataset: null
    }
  },

  computed: {
    ...mapState('base', ['mainApi']),

    ...mapGetters('auth', [
      'userId',
      'platformId'
    ]),

    timeline () {
      const history = this.dataset?.leadJourney || []
      return history
    },

    isBotFinishedOrRefused () {
      return ['bot_finished_sale', 'bot_refusal'].includes(this.dataset?.leadContent?.content?.tabulation)
    }
  },

  watch: {
    leadId: {
      immediate: true,
      handler (newValue) {
        if (newValue !== null) {
          this.fetchData()
        }
      }
    }
  },

  mounted () {
    this.fetchData()
  },

  methods: {
    hide () {
      this.$emit('hide')
    },

    async fetchData () {
      this.isLoading = true

      if (!this.leadId) {
        return
      }

      try {
        const request1 = axios.get(`${this.mainApi}lead/journey/insights/${this.leadId}`)
        const request2 = axios.get(`${this.mainApi}lead/${this.leadId}`)

        const [result1, result2] = await Promise.all([request1, request2])

        this.dataset = {
          leadJourney: this.formatJourneyTimeline(result1.data),
          leadContent: result2.data
        }
      } catch (error) {
        this.dataset = { error }
        throw error
      } finally {
        this.isLoading = false
      }
    },

    formatJourneyTimeline (journeyTimeline) {
      if (!journeyTimeline || !journeyTimeline.history || !journeyTimeline.history.length) {
        return []
      }

      const newJourneyTimeline = journeyTimeline.history.filter((item) => {
        // Remove items with status 1 (lead_pending) and 50 (lead_worked)
        return !([1, 50].includes(item['clh-fk_id_status']))

        // Remove items with 'Tabulação APP' content tabulation value
        && item['clch-value'] !== 'Tabulação APP';
      })

      newJourneyTimeline.reverse()

      return newJourneyTimeline
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/main/components/modal';
@import '~@/styles/main/components/tag-status';
@import '~@/styles/main/components/journey-steps';

.lead-journey {
  &__header {
    margin-top: 8px;
  }

  &__timeline {
    @extend %scrollbar;

    margin-top: 32px;
    max-height: 60vh;

    &__heading {
      font: {
        weight: 700;
        size: 16px;
      }
      line-height: 100%;
    }

    &__steps {
      max-height: 60vh;
      overflow-y: auto;
      padding: 15px 15px 0 5px;
    }

    &__step {
      position: relative;
      display: block;
      padding: 8px 0 8px 20px;
      border-left: 0.8px dashed nth($colors-custom, 2);

      &::before {
        $size: 8px;

        z-index: 10;
        content: '';
        position: absolute;
        top: 50%;
        left: -4px;
        display: block;
        width: $size;
        height: $size;
        border-radius: 100%;
        transform: translateY(-50%);
        background: nth($colors-custom, 4);
      }

      // List bullet point
      // &:first-child {
      //   &::before {
      //     background: nth($colors-custom, 1);
      //   }
      // }

      &:only-of-type {
        border: 0;
      }

      &__container {
        display: flex;
        justify-content: space-between;
        padding: 20px;
        border-radius: 2em;
        background: map-get($colors-util, 'lightest');
      }

      &__label {
        font: {
          weight: 700;
          size: 16px;
        }
        line-height: 140%;
      }

      &__when {
        font: {
          weight: 400;
          size: 16px;
        }
        line-height: 140%;
      }
    }
  }

  &__result {
    padding: 25px;
    margin-top: 32px;
    border: 1px solid map-get($colors-util, 'lighter');
    border-radius: 0 20px 20px 20px;

    &__header {
      font: {
        weight: 400;
        size: 16px;
      }
    }

    &__details {
      font: {
        weight: 400;
        size: 16px;
      }
      line-height: 160%;

      p {
        margin: 20px 0 0;
      }
    }
  }
}
</style>
